// Authentication against the PHP API.
//
// The website signs in exactly the way the app does - phone number, then a
// six-digit code by SMS. There is no password sign-up: the backend only ever
// creates accounts through a verified phone number, so a password form here
// would have nothing to talk to.

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || '';

const TOKEN_KEY = 'cardbox_token';

export type OtpChallenge = {
  challenge_id: string;
  expires_in: number;
  phone_masked: string;
  delivery: string;
};

/** The account shape /auth/me and the verify endpoints return. */
export type Account = {
  id: number;
  phone_e164: string;
  email: string | null;
  display_name: string | null;
  profile_image_url: string | null;
  card_id: number | null;
  card_status: string | null;
};

export type SessionResult = {
  account: Account;
  session: { token: string; expires_at: string };
};

/** Carries the API's error code so a caller can branch on it, not on wording. */
export class AuthError extends Error {
  constructor(message: string, readonly code: string, readonly status: number) {
    super(message);
    this.name = 'AuthError';
  }
}

export function readToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

async function call<T>(path: string, body?: unknown): Promise<T> {
  const token = readToken();
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // A network-level failure, which on this site usually means the API host is
    // unreachable rather than anything the visitor did wrong.
    throw new AuthError('Could not reach the server. Check your connection and try again.', 'network', 0);
  }

  const payload = (await response.json().catch(() => null)) as
    | ({ status?: string; code?: string; message?: string } & T)
    | null;

  if (!response.ok || !payload) {
    throw new AuthError(
      payload?.message || 'Something went wrong. Please try again.',
      payload?.code || 'request_failed',
      response.status,
    );
  }

  return payload;
}

/** `signup` and `login` hit different paths but behave identically. */
export type OtpMode = 'login' | 'signup';

const prefix = (mode: OtpMode) => (mode === 'signup' ? '/auth/signup/phone' : '/auth/phone');

export function requestOtp(phone: string, mode: OtpMode): Promise<OtpChallenge> {
  return call<OtpChallenge>(`${prefix(mode)}/request`, { phone });
}

export function resendOtp(challengeId: string, mode: OtpMode): Promise<OtpChallenge> {
  return call<OtpChallenge>(`${prefix(mode)}/resend`, { challenge_id: challengeId });
}

export function verifyOtp(challengeId: string, otp: string, mode: OtpMode): Promise<SessionResult> {
  return call<SessionResult>(`${prefix(mode)}/verify`, { challenge_id: challengeId, otp });
}

export function fetchAccount(): Promise<{ account: Account }> {
  return call<{ account: Account }>('/auth/me');
}

/**
 * The store predates this API and names its fields differently, so translate
 * once here rather than teaching every consumer both shapes. Role and admin
 * status are not part of /auth/me - admin work happens in the Admin app - so a
 * signed-in visitor is always a plain user on the website.
 */
export function toStoreUser(account: Account) {
  return {
    id: account.id,
    phone_number: account.phone_e164,
    full_name: account.display_name || '',
    role: 'user' as const,
    is_admin: false,
    is_active: true,
    created_at: '',
  };
}

export async function endSession(): Promise<void> {
  try {
    await call('/auth/logout', {});
  } catch {
    // A failed revoke must not trap someone in a signed-in state locally.
  }
}
